import fetchGuestUserRegistrationToken from 'src/services/consumption/revised-subscription-journey/guest-user-registration-service';
import { GQL_QUERIES } from 'src/utils/analytics/newrelic/ErrorTracker';
import {
  getCurrentTimeStamp,
  getTempUserBearerToken,
  getTempUserExpiresIn
} from 'src/utils/common';
import { DynamicObject } from 'src/utils/commonTypes';
import {
  HTTP_HEADER_KEYS,
  HTTP_HEADER_VALUES,
  HTTP_METHODS
} from 'src/utils/constants/HttpMethodsConstants';
import { GUEST_USER_X_TOKEN } from 'src/utils/constants/stringConstants';
import {
  getGuestUserPaymentTransactionId,
  getTempUserTransactionId
} from 'src/utils/ConsumptionHelper/BuyPlanHelper';
import { linkingUrlGenerator } from 'src/utils/UrlGenerator';

import { getLocalStorage } from '../../../utils/browserStorageHandler';
import {
  DATA_COLLECTION,
  GET_USER_TOKEN,
  SEND_OTP,
  SETTINGS,
  VERIFY_OTP,
  APPLE_LOGIN_API,
  FACEBOOK_LOGIN_API,
  FACEBOOK_REGISTRATION_API,
  GOOGLE_LOGIN_API,
  GOOGLE_REGISTRATION_API,
  TWITTER_REGISTER,
  GUEST_USER_LOGIN_URL,
  TWITTER_LOGIN,
  SEND_OTP_CAPTCHA
} from '../../../utils/constants/ApiConstants';
import { GUEST_TOKEN } from '../../../utils/constants/localStorageConstants';
import { getPlatformName } from '../../../utils/UtilityMethods';
import { httpService } from '../../fetch/HttpService';
import {
  SEND_OTP_QUERY,
  USER_EXISTS_QUERY,
  VERIFY_OTP_QUERY,
  VERIFY_OTP_REGISTER_QUERY
} from '../../GraphQL/Queries/Login/LoginRegisterQuery';
import { LoginType } from '../../GraphQL/Queries/types';
import { graphQlRequest } from '../../GraphQL/RequestHandler';
import { IsUserExistsResponeType } from '../../responseTypes/LoginResponse';

export const guestCheckoutRegistrationToken = async (
  key: string,
  value: string
) => {
  const headers = getAPIHeaders();
  // Custom header used because for CONTENT_TYPE default value is application/json
  // but we have to use application/x-www-form-urlencoded
  const customHeader = [
    {
      key: HTTP_HEADER_KEYS.CONTENT_TYPE,
      value: HTTP_HEADER_VALUES.APPLICATION_FORM_URL_ENCODED
    }
  ];
  const reqBody = new URLSearchParams(
    `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
  );

  return await httpService.fetchApiData({
    url: GET_USER_TOKEN,
    type: HTTP_METHODS.POST,
    body: reqBody,
    headers,
    customHeader
  });
};

export const sendOTP = async (key: string, value: string) => {
  const headers = [HTTP_HEADER_KEYS.DEVICE_ID, HTTP_HEADER_KEYS.ESK];
  const reqBody = { [key]: value };
  return await httpService.fetchApiData({
    url: SEND_OTP,
    type: HTTP_METHODS.POST,
    body: reqBody,
    headers
  });
};

export const sendOtpWithCaptcha = async (
  key: string,
  value: string,
  captchaValue: string
) => {
  const headers = getAPIHeaders();
  const reqBody = {
    [key]: value,
    captchadata: {
      captchatype: 'GOOGLE',
      captchavalue: captchaValue
    }
  };
  return await httpService.fetchApiData({
    url: SEND_OTP_CAPTCHA,
    type: HTTP_METHODS.POST,
    body: reqBody,
    headers
  });
};

export const verifyOTP = async (
  otp: string,
  inputType: string,
  inputValue: string
) => {
  const headers = getAPIHeaders();
  const reqBody = {
    otp: otp,
    guest_token: getLocalStorage(GUEST_TOKEN),
    platform: 'PWA',
    device: 'Desktop',
    version: '2.52.51',
    [inputType]: inputValue
  };

  return await httpService.fetchApiData({
    url: VERIFY_OTP,
    type: HTTP_METHODS.POST,
    body: reqBody,
    headers
  });
};

export const registerUserWithOTP = async (
  otp: string,
  inputType: string,
  inputValue: string
) => {
  const headers = getAPIHeaders();
  const isMobile = inputType === LoginType.Mobile;

  const reqBody = {
    [inputType]: inputValue,
    otp: otp,
    guest_token: getLocalStorage(GUEST_TOKEN),
    platform: 'PWA',
    device: 'Desktop',
    version: `${process.env.APP_VERSION}`,
    consents: {
      IsThirdPartyDataSharing: true,
      IsPrivacyPolicy: true,
      IsEmailNotificationEnabled: !isMobile,
      IsSMSNotificationEnabled: isMobile,
      IsWhatsappNotificationEnabled: isMobile,
      IsPushNotificationEnabled: isMobile,
      PolicyVersion: '1.0',
      PlatformName: 'Web'
    }
  };

  return await httpService.fetchApiData({
    url: VERIFY_OTP,
    type: HTTP_METHODS.POST,
    body: reqBody,
    headers
  });
};

export const getDataCollection = async () => {
  const headers = getAPIHeaders();
  return await httpService.fetchApiData({
    url: DATA_COLLECTION,
    type: HTTP_METHODS.GET,
    headers
  });
};

export const getSettings = async () => {
  const headers = getAPIHeaders();
  return await httpService.fetchApiData({
    url: SETTINGS,
    type: HTTP_METHODS.GET,
    headers
  });
};

export const appleLoginAPI = async (reqObj: any) => {
  const headers = getAPIHeaders();
  return await httpService.fetchApiData({
    url: APPLE_LOGIN_API,
    type: HTTP_METHODS.POST,
    body: reqObj,
    headers
  });
};

export const fbLoginAPI = async (reqObj: any) => {
  const headers = getAPIHeaders();
  return await httpService.fetchApiData({
    url: FACEBOOK_LOGIN_API,
    type: HTTP_METHODS.POST,
    body: reqObj,
    headers
  });
};

export const fbRegisterAPI = async (reqObj: any) => {
  const headers = getAPIHeaders();
  return await httpService.fetchApiData({
    url: FACEBOOK_REGISTRATION_API,
    type: HTTP_METHODS.POST,
    body: reqObj,
    headers
  });
};

export const googleLoginAPI = async (reqObj: any) => {
  const headers = getAPIHeaders();
  return await httpService.fetchApiData({
    url: GOOGLE_LOGIN_API,
    type: HTTP_METHODS.POST,
    body: reqObj,
    headers
  });
};

export const googleRegisterAPI = async (reqObj: any) => {
  const headers = getAPIHeaders();
  return await httpService.fetchApiData({
    url: GOOGLE_REGISTRATION_API,
    type: HTTP_METHODS.POST,
    body: reqObj,
    headers
  });
};

export const twitterRegisterAPI = async (reqObj: any) => {
  const headers = getAPIHeaders();
  return await httpService.fetchApiData({
    url: TWITTER_REGISTER,
    type: HTTP_METHODS.POST,
    body: reqObj,
    headers
  });
};

export const twitterLoginAPI = async (reqObj: any) => {
  const headers = getAPIHeaders();
  return await httpService.fetchApiData({
    url: TWITTER_LOGIN,
    type: HTTP_METHODS.POST,
    body: reqObj,
    headers
  });
};

// check token expiry
export const isTokenExpired = () => {
  const expiresInTimeStamp: number = getTempUserExpiresIn() || 0;
  const isexpired = expiresInTimeStamp - getCurrentTimeStamp() < 60;
  return isexpired;
};

// renew User Token
export const renewUserToken = async (token: string, tokenType: string) => {
  switch (tokenType) {
    // guest user X-ACCESS-TOKEN renew
    case GUEST_USER_X_TOKEN:
      return await fetchGuestUserRegistrationToken();
    default:
      return token;
  }
};

// is User Exists
export const isUserExists = async (userId: string) => {
  try {
    const res: IsUserExistsResponeType = await graphQlRequest(
      USER_EXISTS_QUERY,
      {
        mobile: userId
      },
      GQL_QUERIES.USER_EXISTS_QUERY
    );
    return res.data;
  } catch (error) {
    console.error(error);
    return error;
  }
};

// send OTP
type sendOtpRequestType = {
  inputType: string;
  inputValue: string;
  phoneCode?: string;
};

export const sendOtp = async (reqObj: sendOtpRequestType) => {
  const { inputType, inputValue, phoneCode } = reqObj;
  const sendOtpReqObj = {
    sendOtpInput: {
      type: inputType === LoginType.Mobile ? LoginType.Mobile : LoginType.Email,
      value:
        inputType === LoginType.Mobile ? phoneCode + inputValue : inputValue,
      platform: getPlatformName()
    }
  };
  const headers = [HTTP_HEADER_KEYS.DEVICE_ID, HTTP_HEADER_KEYS.ESK];
  try {
    const res = await graphQlRequest(
      SEND_OTP_QUERY,
      sendOtpReqObj,
      GQL_QUERIES.SEND_OTP_QUERY,
      headers
    );
    return res;
  } catch (error) {
    console.error(error);
    return error;
  }
};

// Verify OTP
type verifyOtpRequestType = sendOtpRequestType & { otp: string };
export const verifyOtp = async (reqObj: verifyOtpRequestType) => {
  const headers = [HTTP_HEADER_KEYS.DEVICE_ID, HTTP_HEADER_KEYS.ESK];
  const verifyOtpReqObj = {
    verifyOtpInput: {
      type:
        reqObj.inputType === LoginType.Mobile
          ? LoginType.Mobile
          : LoginType.Email,
      value: reqObj.inputValue,
      otp: reqObj.otp
    }
  };
  try {
    const res = await graphQlRequest(
      VERIFY_OTP_QUERY,
      verifyOtpReqObj,
      GQL_QUERIES.VERIFY_OTP_QUERY,
      headers
    );
    return res;
  } catch (error) {
    console.error(error);
    return error;
  }
};

type registerRequestType = {
  type: string;
  value: string;
  secureToken: string;
};

export const verifyOtpRegister = async (reqObj: registerRequestType) => {
  const headers = [HTTP_HEADER_KEYS.DEVICE_ID, HTTP_HEADER_KEYS.ESK];
  const registerRequestObj = {
    verifyOtpRegisterInput: {
      type: reqObj.type,
      value: reqObj.value,
      secureToken: reqObj.secureToken
    }
  };

  try {
    const res = await graphQlRequest(
      VERIFY_OTP_REGISTER_QUERY,
      registerRequestObj,
      GQL_QUERIES.VERIFY_OTP_REGISTER_QUERY,
      headers
    );
    return res;
  } catch (error) {
    console.error(error);
    return error;
  }
};

type linkingServiceTypes = {
  registerEmail?: string;
  otp?: string;
  requestId?: string;
  secureToken?: string | null | undefined;
  phoneno?: string;
  email?: string;
  registerMobile?: string;
};

export const linkingService = async (
  reqObj: linkingServiceTypes,
  linkingType: string
) => {
  const headers = getAPIHeaders();

  const linkingResponse = await httpService.fetchApiData({
    url: linkingUrlGenerator(linkingType),
    type: HTTP_METHODS.POST,
    body: reqObj,
    headers
  });
  return linkingResponse?.access_token || {};
};
// guest user login service
export const guestUserLogin = async (requestObj: DynamicObject) => {
  let response: DynamicObject = {};
  const headers = [...getAPIHeaders(), HTTP_HEADER_KEYS.X_ACCESS_TOKEN];
  const guestToken = getLocalStorage(GUEST_TOKEN);
  let transactionId = getGuestUserPaymentTransactionId();
  transactionId = transactionId || getTempUserTransactionId() || '';
  requestObj = {
    ...requestObj,
    guest_token: guestToken,
    transaction_id: transactionId
  };
  try {
    if (transactionId) {
      response = await httpService.fetchApiData({
        url: GUEST_USER_LOGIN_URL,
        type: HTTP_METHODS.POST,
        tempToken: getTempUserBearerToken(),
        body: requestObj,
        headers
      });
    }
  } catch (error) {
    console.log('Error while fetching guest user login: ', error);
  }
  return response;
};

export const getAPIHeaders = () => {
  const headers = [
    HTTP_HEADER_KEYS.DEVICE_ID,
    HTTP_HEADER_KEYS.ESK,
    HTTP_HEADER_KEYS.X_Z5_GUEST_TOKEN
  ];
  return headers;
};
